import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
  FIELD_TYPE_SEARCHABLE_DROPDOWN,
} from '/business/shared/components/questions/helper/fieldEnums';

import StyledParagraph from '/shared/styled/components/StyledParagraph';
import useAppContext from '/business/shared/hooks/useAppContext';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

type SearchableDropdownValue = {
  inputValue?: string | null;
};

export default function useDeicingSchema({
  deicingChemicalTypes,
  answerHasNoData,
  hasAnswer,
}: {
  deicingChemicalTypes: EmissionTypeOption[];
  answerHasNoData: boolean;
  hasAnswer: boolean;
}) {
  const { t } = useTranslation();
  const { questionsPage } = useAppContext();
  const [valueChemicalType, setValueChemicalType] = useState<string | null>(null);
  const [valueHasNoData, setValueHasNoData] = useState<boolean | undefined>(false);
  const [forceSubmitForm, setForceSubmitForm] = useState(false);

  const validationValue = questionsPage?.hasCustomValidationValues
    ? (questionsPage.validationValues?.deicing ?? 99999)
    : 99999;

  const chemicalType = useMemo(() => {
    return {
      type: FIELD_TYPE_SEARCHABLE_DROPDOWN,
      element: 'chemical_type',
      propName: 'chemicalType',
      labelI18nKey: 'business:QuestionPage.forms.deicing.para1',
      placeholderI18nKey: 'business:QuestionPage.forms.deicing.dropdownTitle',
      dropdownTitleI18nKey: 'business:QuestionPage.forms.deicing.dropdownTitle',
      saveMeCallback: (value: SearchableDropdownValue) => {
        const { inputValue: deicingChemicalTypeslug } = value;

        return {
          chemicalType: deicingChemicalTypes.find(({ name }) => name === deicingChemicalTypeslug),
          hasChemicalType: true,
        };
      },
      values: deicingChemicalTypes,
      styleInputParentWidth: '20rem',
      onChangeCallback: (value: string | null) => {
        setValueChemicalType(value);
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.deicing.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.deicing.notFound'),
        },
      ],
    };
  }, [valueHasNoData, deicingChemicalTypes, t]);

  const quantity = useMemo(() => {
    const chemicalUnits = deicingChemicalTypes.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit !== 'string') {
          return typeUnit;
        }
        const transLitDisplay = t('business:emissionFactors.volumes.'.concat(typeUnit), typeUnit);
        return {
          name: typeUnit,
          displayName: transLitDisplay,
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      placeholderI18nKey: 'business:QuestionPage.forms.deicing.quantityPlaceholder',
      styleInputParentWidth: '20rem',
      value: (
        <StyledParagraph $smallText $grey $hasExtraMarginBottom>
          Can't find your de-icing chemical type or unsure which to use? It should be listed on your maintenance
          reports.
        </StyledParagraph>
      ),
      dropdown: {
        element: 'chemical_unit',
        values: chemicalUnits?.find(({ name }) => name === valueChemicalType)?.units,
        propName: 'chemicalUnit',
      },
      labelI18nKey: 'business:QuestionPage.forms.deicing.para2',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.deicing.provideQty'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.deicing.qtyTooLow'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.deicing.qtyTooHigh'),
          value: validationValue,
        },
      ],
    };
  }, [validationValue, deicingChemicalTypes, valueChemicalType, t]);

  const dataSource = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.deicing.para5',
      description: <></>,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema = useMemo(() => [chemicalType, quantity, dataSource], [chemicalType, quantity, dataSource]);
  return {
    pageSchema,
    forceSubmitForm,
    setForceSubmitForm,
  };
}
